import { COLOR } from '@/utils/color';
import {
  CheckCircleOutlined,
  CheckCircleTwoTone,
  CloseCircleOutlined,
  CloseCircleTwoTone,
  ExclamationCircleOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { Divider, Row, Tag, Tooltip, Col, Space, Button, Popover } from 'antd';
import React, { useEffect } from 'react';
import styles from './StatusConnections.module.css';
import { connect } from 'dva';
import { Icon } from '@ant-design/compatible';
import Label from '@/components/Label/Label';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { Link } from 'dva/router';

import { router } from 'umi';
import { converageConnections } from '@/utils/constant';
import { formatDateAndTime } from '@/utils/utils';
import { PATH_DASHBOARD } from '@/utils/routes/path';

const isLastIndex = (index, array) => {
  return index === array.length - 1;
};

const handleTagClick = id => {
  router.push(`${PATH_DASHBOARD.setup.cloudAccountsEdit}/?cloudaccount=${id}`);
};

const getConnectionStatus = (connections, provider, isActive, protectionLevel, id = '') => {
  // if cloud account is not active then return stop icon
  if (!isActive)
    return (
      <Icon
        type="stop"
        style={{
          fontSize: '1rem',
          color: COLOR.DISABLED,
        }}
      />
    );

  // ?if provider is not aws then return 200 status as we don't have any connections for other providers
  if (provider.toLowerCase() === 'azure' && isEmpty(connections))
    return <CheckCircleTwoTone style={{ fontSize: '1rem' }} twoToneColor={COLOR.GREEN} />;

  // if connections not available then return 500
  if (isEmpty(connections))
    return (
      <Icon
        type="stop"
        style={{
          fontSize: '1rem',
          color: COLOR.DISABLED,
        }}
      />
    );

  let filteredConnections = [];
  if (protectionLevel === 'basic') {
    // filter the connections that are lies in basic mode, we can compare from coverageConnections
    const filteredConnectionsKeys = Object.keys(connections).filter(connection =>
      converageConnections.find(
        converageConnection =>
          converageConnection.key === connection && converageConnection.mode === 'basic'
      )
    );

    filteredConnectionsKeys.forEach(connectionKey => {
      filteredConnections.push(connections[connectionKey]);
    });
  } else {
    filteredConnections = Object.values(connections);
  }

  // if all connections status is 200 then return success icon
  const isAllPassed = filteredConnections.every(connection => connection.status === 200);
  if (isAllPassed)
    return (
      <Tag
        color="success"
        icon={<CheckCircleOutlined />}
        style={{ fontSize: '0.6rem' }}
        onClick={() => handleTagClick(id)}
      >
        Connected
      </Tag>
    );

  // if all connections status is 500 then return error icon
  const isAnyFailed = filteredConnections.some(connection => connection.status === 500);
  if (isAnyFailed)
    return (
      <Tag
        color="error"
        icon={<CloseCircleOutlined />}
        style={{ fontSize: '0.7rem' }}
        onClick={() => handleTagClick(id)}
      >
        Error
      </Tag>
    );

  // if any connection status is 500 then return warning icon
  const isAnyRejected = filteredConnections.some(connection => connection.status !== 200);
  if (isAnyRejected)
    return (
      <Tag
        color="warning"
        icon={<ExclamationCircleOutlined />}
        style={{ fontSize: '0.7rem' }}
        onClick={() => handleTagClick(id)}
      >
        Warning
      </Tag>
    );
};

const StatusConnections = ({
  testConnectionTypes,
  lastHealthCheck,
  provider,
  lastScan,
  isActive,
  protectionLevel,
  id,
}) => {
  return (
    <Popover
      placement="bottom"
      content={
        isActive ? (
          <Content
            connections={testConnectionTypes}
            lastHealthCheck={lastHealthCheck}
            provider={provider}
            lastScan={lastScan}
            protectionLevel={protectionLevel}
          />
        ) : (
          'This Cloud account is disabled.'
        )
      }
    >
      {getConnectionStatus(testConnectionTypes, provider, isActive, protectionLevel, id)}
    </Popover>
  );
};

export default StatusConnections;

const Content = ({ connections, lastHealthCheck, provider, lastScan, protectionLevel }) => {
  const getLastHealthCheck = () => {
    if (isEmpty(lastHealthCheck))
      return `Last Checked: ${formatDateAndTime(lastScan?.toDate()).exactTime}`;

    return `Last Checked: ${formatDateAndTime(lastHealthCheck?.toDate()).exactTime}`;
  };

  const getStatusIcon = (status, isLoading) => {
    if (isLoading) return <LoadingOutlined style={{ fontSize: '1rem' }} />;
    if (status === 200)
      return <CheckCircleTwoTone style={{ fontSize: '1rem' }} twoToneColor={COLOR.GREEN} />;
    if (status === 500)
      return <CloseCircleTwoTone style={{ fontSize: '1rem' }} twoToneColor={COLOR.RED} />;

    return <ExclamationCircleOutlined style={{ fontSize: '1rem', color: COLOR.YELLOW }} />;
  };

  const getModifedConnections = (connections, provider, protectionLevel) => {
    // ?if provider is not aws then return just basic connections as we don't have any connections for other providers
    if (provider.toLowerCase() === 'azure' && !connections) {
      return converageConnections.filter(connection => connection.mode === 'basic');
    }

    const modifiedConnections = [];
    // * if provider is aws then return all connections with their status
    // * converageConnections is the list of connections that we have for the cloud account
    if (!isEmpty(connections)) {
      for (const connection of converageConnections) {
        let connectionAvailable;
        if (protectionLevel === 'basic') {
          connectionAvailable = Object.keys(connections).find(
            availableConnection =>
              availableConnection === connection.key && connection.mode === 'basic'
          );
        } else {
          connectionAvailable = Object.keys(connections).find(
            availableConnection => availableConnection === connection.key
          );
        }

        if (connectionAvailable) {
          modifiedConnections.push({
            ...connection,
            ...connections[connectionAvailable],
          });
        }
      }
    }

    return modifiedConnections;
  };

  return (
    <>
      {getModifedConnections(connections, provider, protectionLevel).length === 0 && 'N/A'}

      {getModifedConnections(connections, provider, protectionLevel).length > 0 && (
        <div
          style={{
            width: '240px',
          }}
        >
          {getModifedConnections(connections, provider, protectionLevel).map(
            (connection, index) => {
              return (
                <>
                  <Row
                    style={{
                      padding: '10px',
                    }}
                  >
                    <Col span={24}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <div
                          style={{
                            marginRight: 20,
                          }}
                        >
                          <p className={styles['status_text']}>{connection.name}</p>
                        </div>
                        <div>{getStatusIcon(connection.status, connection.loding)}</div>
                      </div>
                    </Col>
                  </Row>
                  {!isLastIndex(
                    index,
                    getModifedConnections(connections, provider, protectionLevel)
                  ) && (
                    <Divider
                      dashed
                      style={{
                        margin: 0,
                      }}
                    />
                  )}
                </>
              );
            }
          )}
          <Row
            style={{
              fontSize: 12,
              opacity: 0.45,
              color: 'rgba(0, 0, 0, 0.85)',
              marginTop: 18,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Icon type="clock-circle" style={{ marginRight: 8 }} />
            {getLastHealthCheck()}
          </Row>
        </div>
      )}
    </>
  );
};
